'use client';

import { motion } from 'framer-motion';
import { Box, Typography, Container, Button, Grid, useTheme } from '@mui/material';
import Link from 'next/link';

export default function HomePage() {
  const theme = useTheme();
  
  const staggeredContainer = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.3,
        ease: 'easeInOut',
      },
    },
  };

  const staggeredItem = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      {/* Hero Section */}
      <Box
        component={motion.section}
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 1.2,
          ease: 'easeOut',
        }}
        sx={{
          py: { xs: 8, sm: 12 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Hero Content */}
        <Container maxWidth="md">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: 1.5,
              ease: 'easeOut',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                mb: 2,
                background: (theme) => theme.custom.gradients.secondary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Welcome to My Journey!
            </Typography>
          </motion.div>

          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{
              duration: 1.2,
              delay: 0.5,
              ease: 'easeOut',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                color: 'white',
                opacity: 0.8,
              }}
            >
              A testament to achieving extraordinary things with minimal tools.
            </Typography>
          </motion.div>

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: 1,
              delay: 1,
              ease: 'easeOut',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                <Button
                  component={Link}
                  href="/about"
                  variant="contained"
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  Read My Story
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                <Button
                  component={Link}
                  href="/repos"
                  variant="outlined"
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': { bgcolor: 'grey.800' },
                  }}
                >
                  My Imagination
                </Button>
              </motion.div>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* About Section */}
      <Box
        component={motion.section}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggeredContainer}
        sx={{
          py: { xs: 8, sm: 12 },
          position: 'relative',
          px: 2,
          textAlign: 'center',
        }}
      >
        {/* Glassmorphism Effect */}
        <Box
          sx={{
            ...theme.custom.mixins.glassmorphism,
            mx: 'auto',
            px: { xs: 4, sm: 6 },
            py: { xs: 6, sm: 8 },
            borderRadius: '16px',
            maxWidth: '720px',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Container maxWidth="md">
            <motion.div variants={staggeredItem}>
              <Typography
                variant="h2"
                sx={{
                  mb: 4,
                  textAlign: 'center',
                  color: (theme) => theme.palette.text.primary,
                  textShadow: '0 2px 6px rgba(0, 0, 0, 0.3)', // Improve readability
                }}
              >
                A Bit About Me
              </Typography>
            </motion.div>
            <motion.div variants={staggeredItem}>
              <Typography
                sx={{
                  mb: 4,
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                  color: (theme) => theme.palette.text.primary, // Better contrast
                  textShadow: '0 1px 4px rgba(0, 0, 0, 0.2)', // Subtle shadow for better readability
                }}
              >
                I am a principled thinker, software engineer, and solutions architect with a passion for modular, containerized AI solutions.
              </Typography>
            </motion.div>
            <motion.div variants={staggeredItem}>
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  component={Link}
                  href="/"
                  sx={{
                    color: 'primary.main',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Visit My Blog
                </Button>
              </Box>
            </motion.div>
          </Container>
        </Box>
      </Box>
      
      {/* Featured Sections */}
      <Box
        component={motion.section}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggeredContainer}
        sx={{
          py: { xs: 8, sm: 12 },
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <motion.div variants={staggeredItem}>
            <Typography variant="h2" sx={{ mb: 4, textAlign: 'center' }}>
              Explore Popular Sections
            </Typography>
          </motion.div>
          <Grid container spacing={4}>
            {['Tech Articles', 'AI Projects', 'Personal Insights'].map((title, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <motion.div variants={staggeredItem}>
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: '8px',
                      boxShadow: 3,
                      textAlign: 'center',
                      '&:hover': { boxShadow: 6 },
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {title}
                    </Typography>
                    <Button variant="text" href="#">
                      Learn More
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
}